import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import Button from '@/components/Button';
import RangeDatePicker from '@/components/RangeDatePicker/RangeDatePicker';

import type { IFalabellaFilters } from '../../useFalabella';

import { falabellaModalityOptions, falabellaStatusOptions } from '../../constants';

interface FiltersProps {
  selectedFilters: IFalabellaFilters;
  onChange: (filterName: keyof IFalabellaFilters, value: string | null) => void;
  clearFilters: () => void;
  applyFilters: () => void;
}

const Filters = (props: FiltersProps) => {
  const theme = useTheme();
  const { t } = useTranslation('falabella');

  const commonPaperStyle = {
    p: 2.5,
    mb: 2,
    borderRadius: 2,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: 'background.paper',
  };

  return (
    <Box sx={{ mt: 2, width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      {/* 1. Estatus Falabella */}
      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          {t('filters.status', 'Estatus Falabella')}
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={props.selectedFilters.status || ''}
          onChange={(e) => props.onChange('status', e.target.value)}
        >
          <MenuItem value="">{t('filters.all', 'Todos')}</MenuItem>
          {falabellaStatusOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* 2. Modalidad de envío */}
      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          {t('filters.modality', 'Modalidad de envío')}
        </Typography>
        <TextField
          select
          fullWidth
          size="small"
          value={props.selectedFilters.modality || ''}
          onChange={(e) => props.onChange('modality', e.target.value)}
        >
          <MenuItem value="">{t('filters.all', 'Todos')}</MenuItem>
          {falabellaModalityOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>
      </Paper>

      {/* 3. Fecha de creación del pedido */}
      <Paper elevation={0} sx={commonPaperStyle}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
          {t('filters.creationDate', 'Fecha de creación del pedido')}
        </Typography>
        <RangeDatePicker
          startLabel={t('filters.dateFrom', 'Desde')}
          endLabel={t('filters.dateTo', 'Hasta')}
          onChange={(name, value) => {
            props.onChange(name === 'startDate' ? 'dateFrom' : 'dateTo', value);
          }}
          startValue={props.selectedFilters.dateFrom}
          endValue={props.selectedFilters.dateTo}
          untilToday
        />
      </Paper>

      {/* 3. Botones de acción */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexWrap: 'wrap',
          '& > button': {
            minWidth: '140px',
            px: 3,
            py: 1,
            [theme.breakpoints.down('sm')]: { width: '100%' },
          },
        }}
      >
        <Button onClick={props.clearFilters} variant="outlined">
          {t('filters.clear', 'Borrar filtros')}
        </Button>
        <Button onClick={props.applyFilters} variant="contained">
          {t('filters.apply', 'Aplicar filtros')}
        </Button>
      </Box>
    </Box>
  );
};

export default Filters;
